import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Row, Col, Nav, Tab, Spinner, Alert } from 'react-bootstrap';
import { FaHeart, FaComment, FaEye } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import { getAssetUrl } from '../../../utils/apiBase';
import { useAuth } from '../../../contexts/AuthContext';
import { encodeForumId } from '../../../utils/urlEncryption';

const UserForumList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [forums, setForums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchForums();
  }, [activeTab, currentPage]);

  const fetchForums = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page: currentPage,
        limit: 12,
        filter: activeTab === 'my-org' ? 'organization' : 'all'
      };

      const response = await api.forums.getAll(params);
      
      if (response.data.success) {
        setForums(response.data.data.forums);
        setTotalPages(response.data.data.totalPages);
      } else {
        setError(response.data.message || 'Failed to fetch forums');
      }
    } catch (error) {
      console.error('Error fetching forums:', error);
      setError('Failed to fetch forums. Please try again.');
      toast.error('Failed to fetch forums');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFilterTabs = () => {
    return [
      { key: 'all', label: 'All Forums', count: null },
      { key: 'my-org', label: 'My Organization', count: null }
    ];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-purple-600 border-t-transparent mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">Loading forums...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-4 sm:p-6 md:p-8 lg:p-10">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl px-5 py-4 sm:p-6 border border-purple-100 shadow-xl">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-white text-xl sm:text-2xl shadow-md">
              <FaComment />
            </div>
            <div>
              <h2 className="text-left text-2xl sm:text-2xl font-bold text-gray-900 mb-1">Forum Discussions</h2>
              <p className="text-gray-600 text-sm sm:text-base m-0">
                Join conversations and share your thoughts
              </p>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4 rounded-xl border-2 border-red-200 bg-red-50">
          <div className="flex items-center gap-3">
            <i className="fas fa-exclamation-triangle text-red-600 text-xl"></i>
            <span className="font-semibold">{error}</span>
          </div>
        </Alert>
      )}

      <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-purple-100 shadow-xl p-4 sm:p-6">
        <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
          <Nav variant="tabs" className="mb-4 border-0">
            {getFilterTabs().map(tab => (
              <Nav.Item key={tab.key}>
                <Nav.Link 
                  eventKey={tab.key}
                  className={`rounded-xl me-2 mb-2 font-semibold transition-all duration-200 ${
                    activeTab === tab.key 
                      ? 'bg-gradient-to-r from-purple-600 to-indigo-600 shadow-md' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {tab.label}
                  {tab.count !== null && (
                    <Badge className={`ms-2 ${
                      activeTab === tab.key ? 'bg-white/30 text-white' : 'bg-purple-600 text-white'
                    }`}>
                      {tab.count}
                    </Badge>
                  )}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>

          <Tab.Content>
            <Tab.Pane eventKey={activeTab}>
              <Row className="g-4">
                {forums.length === 0 ? (
                  <Col>
                    <div className="text-center py-12 sm:py-16">
                      <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                        <FaComment size={32} className="text-white" />
                      </div>
                      <h5 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">No forums found</h5>
                      <p className="text-gray-600 text-base sm:text-lg">There are no forums available at the moment.</p>
                    </div>
                  </Col>
                ) : (
                  forums.map(forum => (
                    <Col key={forum.id} lg={4} md={6} sm={12}>
                      <Card className="h-100 border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                        {forum.attachment && (
                          <div className="position-relative">
                            <Card.Img
                              variant="top"
                              src={getAssetUrl(forum.attachment)}
                              style={{ height: '200px', objectFit: 'cover' }}
                            />
                            <Badge
                              className={`position-absolute top-2 end-2 px-3 py-1 rounded-full shadow-md font-semibold ${
                                forum.org_id 
                                  ? 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white' 
                                  : 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white'
                              }`}
                            >
                              {forum.org_id ? 'Organization' : 'Global'}
                            </Badge>
                          </div>
                        )}
                        <Card.Body className="d-flex flex-column p-4">
                          <Card.Title className="h6 mb-2 fw-bold text-gray-900" style={{ 
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            minHeight: '48px'
                          }}>
                            {forum.title}
                          </Card.Title>
                          <Card.Text className="text-gray-600 small flex-grow-1 mb-3" style={{
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            minHeight: '60px'
                          }}>
                            {forum.description}
                          </Card.Text>
                          <div className="d-flex justify-content-between align-items-center mb-3 p-2 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg">
                            <div className="d-flex gap-3">
                              <small className="text-gray-700 d-flex align-items-center font-semibold">
                                <FaHeart className="me-1 text-red-500" />
                                {forum.likes_count}
                              </small>
                              <small className="text-gray-700 d-flex align-items-center font-semibold">
                                <FaComment className="me-1 text-purple-600" />
                                {forum.comments_count || 0}
                              </small>
                              <small className="text-gray-700 d-flex align-items-center font-semibold">
                                <FaEye className="me-1 text-blue-600" />
                                {forum.views_count}
                              </small>
                            </div>
                          </div>
                          <div className="d-flex justify-content-between align-items-center mb-3 text-sm">
                            <small className="text-gray-600">
                              by <span className="font-semibold text-gray-900">{forum.creator?.first_name} {forum.creator?.last_name}</span>
                            </small>
                            <small className="text-gray-600">
                              {formatDate(forum.createdAt)}
                            </small>
                          </div>
                          <div className="mt-auto">
                            <Button
                            bsPrefix="custom-hamburger"
                              className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
                             
                              onClick={() => navigate(`/user/forums/${encodeForumId(forum.id)}`)}
                            >
                              View Discussion
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))
                )}
              </Row>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-6">
                  <nav>
                    <ul className="pagination mb-0">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link rounded-lg me-2 border-0 shadow-sm hover:shadow-md transition-all duration-200"
                          style={{ 
                            backgroundColor: currentPage === 1 ? '#e5e7eb' : 'white',
                            color: currentPage === 1 ? '#9ca3af' : '#6366f1',
                            fontWeight: '600'
                          }}
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                      </li>
                      {[...Array(totalPages)].map((_, index) => (
                        <li key={index + 1} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                          <button
                            className={`page-link rounded-lg me-2 border-0 shadow-sm hover:shadow-md transition-all duration-200 ${
                              currentPage === index + 1 
                                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                            style={{ fontWeight: '600' }}
                            onClick={() => setCurrentPage(index + 1)}
                          >
                            {index + 1}
                          </button>
                        </li>
                      ))}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link rounded-lg border-0 shadow-sm hover:shadow-md transition-all duration-200"
                          style={{ 
                            backgroundColor: currentPage === totalPages ? '#e5e7eb' : 'white',
                            color: currentPage === totalPages ? '#9ca3af' : '#6366f1',
                            fontWeight: '600'
                          }}
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </div>
    </div>
  );
};

export default UserForumList;
